import { createBrowserHistory } from 'history';

import * as $ from "jquery";
import algoliasearch from 'algoliasearch';
import algoliasearchhelper, { AlgoliaSearchHelper } from 'algoliasearch-helper';
import type { SearchResults } from 'algoliasearch-helper';
import ellipsize from 'ellipsize';
import mustache from 'mustache';
import parse from 'url-parse';
import browser from "../../utils/browser";
import url from '../../utils/url';
import insights from 'search-insights';
import Environment from "../../common/environment";

function debounce(func: Function, wait: number, immediate?: boolean) {
  let timeout: NodeJS.Timeout | null;
  return function () {
    // @ts-ignore
    const context = this;
    const args = arguments;

    timeout && clearTimeout(timeout);
    timeout = setTimeout(function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    }, wait);
    if (immediate && !timeout) func.apply(context, args);
  };
}

/**
 * @type {Object}
 */
const history = createBrowserHistory();

/**
 * Maximum number of pages to load automatically on scroll.
 * @type {number}
 */
const MAX_AUTO_LOAD_PAGES = 2;

const SEARCH_STATE = {
  error: 'wac-search__state--error',
  idle: 'wac-search__state--idle',
  hasMoreResults: 'wac-search__state--has-more-results',
  loading: 'wac-search__state--loading',
  noMoreResults: 'wac-search__state--no-more-results',
  noResults: 'wac-search__state--no-results'
};

const ERROR_STATE = 'error';
const IDLE_STATE = 'idle';
const HAS_MORE_RESULTS_STATE = 'hasMoreResults';
const LOADING_STATE = 'loading';

const CONTAINER_LOADING_CLASS = 'loading';
const ERROR_TEMPLATE_ID = 'wac-search__error-template';
const EVENT_SCROLL_AUTOLOAD_RESULTS = 'scroll.loadNextPage';
const FACET_LOAD_CLASS = 'load';
const FACET_SELECTED_CLASS = 'selected';
const FACETS_TEMPLATE_ID = 'wac-search__search-facets-template';
const FIXED_MENU_BOTTOM_CLASS = 'fixed-menu-bottom';
const FIXED_MENU_CLASS = 'fixed-menu';
const NO_RESULTS_TEMPLATE_ID = 'wac-search__no-results-template';
const RESULTS_LINK_CLASS = 'results-wrapper--organic';
const RESULTS_TEMPLATE_ID = 'wac-search__search-results-template';
const SNIPPET_MAX_LENGTH = 300;

// Algolia stuff
const ALGOLIA_APP_ID = 'CU5KFWE75C';
const ALGOLIA_SEARCH_API_KEY = '8cb0497a0549da256319b1b8178e5421';
const ALGOLIA_SEARCH_INDEX_NAME = (Environment.getEnvironment() === "production") ? "www.atlassian.com.v2" : "www.atlassian.com.v2_dev";
const ALGOLIA_FACET_KEY = 'category';
const ALGOLIA_SNIPPET_KEY = 'description';
const algoliaSearchClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_API_KEY);
// Initialize Algolia Insights
insights('init', {
  appId: ALGOLIA_APP_ID,
  apiKey: ALGOLIA_SEARCH_API_KEY
});
const locale = Environment.getInternationalSubdirectory() || 'en';

function sendClickEvent(queryId: string, objectId: string, position: number) {
  insights('clickedObjectIDsAfterSearch', {
    eventName: 'Result Clicked',
    objectIDs: [objectId],
    positions: [Number(position)],
    queryID: queryId,
    index: ALGOLIA_SEARCH_INDEX_NAME
  });
}

class WacSearch {
  $searchContainer: JQuery<HTMLElement>;
  $facetsList: JQuery<HTMLElement>;
  $loadButton: JQuery<HTMLElement>;
  $results: JQuery<HTMLElement>;
  $searchForm: JQuery<HTMLElement>;
  $searchInput: JQuery<HTMLElement>;
  $globalHeader: JQuery<HTMLElement>;
  $globalFooter: JQuery<HTMLElement>;
  hasNextPage: boolean;
  searchHelper: AlgoliaSearchHelper;
  activeQuery: any;
  activeFacet: any;

  constructor() {
    this.$searchContainer = $('#atlassianSearch');

    this.$facetsList   = $('#wac-search__facet-list');
    this.$loadButton   = $('#wac-search__load-results-button');
    this.$results      = $('#wac-search__search-results-container');
    this.$searchForm   = $('#wac-search__search-page-form');
    this.$searchInput  = $('#wac-search__search-page-input');
    this.$globalHeader = $('body > header');
    this.$globalFooter = $('body > footer');
    this.hasNextPage   = false;

    this.searchHelper = algoliasearchhelper(algoliaSearchClient, ALGOLIA_SEARCH_INDEX_NAME, {
      facetFilters: [`locale:${locale}`],
      facets: [ALGOLIA_FACET_KEY],
      clickAnalytics: true // Enables click analytics
    });
  }

  /**
   * Initialize the "search" application.
   */
  init() {
    var urlVal = parse(window.location, true);

    this.activeQuery = urlVal.query.q ? url.htmlEscape(encodeURIComponent(urlVal.query.q)) : null;
    this.activeFacet = urlVal.query.f ? url.htmlEscape(encodeURIComponent(urlVal.query.f)) : null;

    this.bindEvents();
  }

  /**
   * Do all the things!
   */
  bindEvents() {

    /**
     * When the page loads with a "query" or (optionally) a "facet" URL parameters,
     * the page's search form should reflect these inputs as well as display results for them.
     */

    this.search(this.activeQuery, this.activeFacet);

    /**
     * When a facet is clicked on,
     * the all facets will be removed from the query and the results updated.
     */
    this.$facetsList.on('click', 'a', (e) => {

      e.preventDefault();

      const $facetLink = $(e.currentTarget);
      const query = $facetLink.attr('data-q');
      const facet = $facetLink.attr('data-f');

      let newUrl = `?q=${query}`;
      if (facet) {
        newUrl += `&f=${facet}`;
      }

      history.push(newUrl);
    });

    /**
     * When the URL changes, update the search results.
     */
    history.listen(({ action, location }) => {

      if (action === 'PUSH' || action === 'POP') {

        const urlVal = parse(location.search, true);
        const query = urlVal.query.q ? url.htmlEscape(encodeURIComponent(urlVal.query.q)) : null;
        const facet = urlVal.query.f ? url.htmlEscape(encodeURIComponent(urlVal.query.f)) : null;

        $('title').text(`${query} | Search | Atlassian`);

        this.activeQuery = query;
        this.activeFacet = facet;

        this.search(query, facet);
      }
    });

    /**
     * Hijax search form submission.
     */
    this.$searchForm.on('submit', e => {

      e.preventDefault();

      const serializeFormData = $(e.currentTarget).serialize();
      const decodedSerializeFormData = decodeURIComponent(serializeFormData);

      history.push(`?${decodedSerializeFormData}`);
    });

    /**
     * Manually load more results when clicking button.
     */
    this.$loadButton.on('click', this.$searchContainer, () => {
      this.goToNextPage();
    });

    /**
     * Make the facet nav sticky.
     */
    $(window).on('scroll', this.repositionFacetNav.bind(this));

    /**
     * Assign width to facets list so it renders properly when 'display: fixed' is applied
     */
    if (!browser.isMobile()) {
      this.setFacetWidth();
    }

    const screenWidth = $(window).width();
    $(window).on("resize", debounce(() => {
      if ($(window).width() !== screenWidth) {
        this.setFacetWidth();
      }
    }, 500));

    /**
     * Do all the Algoia things!
     */
    this.bindAlgoliaEvents();
  }

  /**
   * Bind Algolia events.
   */
  bindAlgoliaEvents() {

    /**
     * Do this at the end of every search or error.
     * @param {Object} results
     * @param {Object} error
     */
    const onCompleteSearch = (results: SearchResults | null, error?: Error) => {
      this.$searchContainer.removeClass(CONTAINER_LOADING_CLASS);
      this.$facetsList.find('a').removeClass(FACET_LOAD_CLASS);

      if (error) {
        this.setState(ERROR_STATE);
      } else if (!results?.nbHits) {
        this.setState(IDLE_STATE);
      } else {
        if (this.hasNextPage) {
          if (results.page >= MAX_AUTO_LOAD_PAGES) {
            this.setState(HAS_MORE_RESULTS_STATE);
          } else {
            this.setState(LOADING_STATE);
          }
        } else {
          this.setState(IDLE_STATE);
        }
      }
    };

    /**
     * The "this.searchHelper" object maintains the status of the search results.
     * Do this any time it changes.
     */
    this.searchHelper.on('change', () => {
      // Refresh the facet links
      this.renderFacetsNav();
    });

    /**
     * Do this as soon as you send the search request.
     */
    this.searchHelper.on('search', ({ state }) => {

      // Display "loading" icon next to the facet link
      let $facetLink;
      if (state.facetsRefinements.hasOwnProperty(ALGOLIA_FACET_KEY)) {
        const facet = state.facetsRefinements[ALGOLIA_FACET_KEY][0];
        $facetLink = this.$facetsList.find(`a[data-f="${facet}"]`);
      } else {
        $facetLink = this.$facetsList.find('a').first();
      }
      $facetLink.addClass(FACET_LOAD_CLASS);
    });

    /**
     * Do this when you get a search response.
     */
    this.searchHelper.on('result', ({ results, state }) => {
      this.hasNextPage = (results.nbPages - 1) > results.page;

      // currentPage is undefined until nextPage() is called
      const currentPage = this.searchHelper.getPage() ?? 0;

      // If we got any   results
      if (results.nbHits) {
        const hits = Object.keys(results.hits).map(index => {
          let hit = results.hits[index as unknown as number];

          if (hit.hasOwnProperty(ALGOLIA_SNIPPET_KEY)) {
            hit[ALGOLIA_SNIPPET_KEY] = ellipsize(hit[ALGOLIA_SNIPPET_KEY], SNIPPET_MAX_LENGTH);
              hit["position"] = Number(index) + 1 + (currentPage * 20);
          }

          return hit;
        }) as unknown as SearchResults;

        // If this is a new query
        if (results.page == 0) {
          this.renderFacetsNav();

          this.renderResults(hits, results.queryID as string);

          // Clear currently highlighted facet
          this.$facetsList.find(`a.${FACET_SELECTED_CLASS}`).removeClass(FACET_SELECTED_CLASS);

          // Highlight selected facet
          let $facetLink;
          if (this.activeFacet) {
            $facetLink = this.$facetsList.find(`a[data-f="${this.activeFacet}"]`);
          } else {
            $facetLink = this.$facetsList.find('a').first();
          }
          $facetLink.addClass(FACET_SELECTED_CLASS);

          // Activate autoloading if there are more pages
          if (this.hasNextPage) {
            this.initAutoloadResults();
          }
        }

        // If it's not a new query, then we're fetching the next page.
        else {
          this.renderNextPage(hits);

          // Stop autoloading results if we can't or don't want to.
          if (state.page === MAX_AUTO_LOAD_PAGES || !this.hasNextPage) {
            $(window).off(EVENT_SCROLL_AUTOLOAD_RESULTS);
          }
        }
      } else {
        this.setState(IDLE_STATE);
        this.renderNoResults();
      }

      onCompleteSearch(results);
    });

    /**
     * Something went wrong with the query, do something about it.
     * This can be triggered by, for example, providing a wrong index name.
     */
    this.searchHelper.on('error', ({ error }) => {
      onCompleteSearch(null, error);
      this.renderError();
    });
  }

  /**
   * Increase the current page by 1, then search for the corresponding results.
   */
  goToNextPage() {
    this.searchHelper.nextPage();
    this.searchHelper.search();
  }

  /**
   * Automatically load more results as you scroll down.
   * This is triggered when you scroll to the top of the las result.
   */
  initAutoloadResults() {
    $(window).on(EVENT_SCROLL_AUTOLOAD_RESULTS, debounce(() => {
      if (this.$results.find(`.${RESULTS_LINK_CLASS}`).length) {
        if (window.scrollY + $('body').height() > this.$results.find(`.${RESULTS_LINK_CLASS}`).eq(-8).offset().top) {
          this.goToNextPage();
        }
      }
    }, 600));
  }

  /**
   * Render generic error message.
   */
  renderError() {
    const template = document.getElementById(ERROR_TEMPLATE_ID)?.innerHTML || '';
    this.$results.html(template);
  }

  /**
   * Render facet navigation.
   */
  renderFacetsNav() {

    // This is a one-off search independent of the actual search results.
    // We want to display all the available facets of the given query, that's why
    // we have to perform a one-off search; we remove the active "facet" so nothing
    // is filtered out.
    this.searchHelper.searchOnce({
      hitsPerPage: 1,
      facetsRefinements: {},
      query: this.activeQuery
    }, (error, content) => {
      const facets = content.getFacetValues(ALGOLIA_FACET_KEY, {sortBy: ['name:asc']});

      if (facets.length) {
        const template = $(`#${FACETS_TEMPLATE_ID}`).html();
        const output = mustache.render(template, {
          facets: facets,
          query: this.activeQuery
        });

        this.$facetsList.html(output);
      } else {
        this.$facetsList.html('');
      }
    });
  }

  /**
   * Append the results of the next page to the bottom of the results.
   * @param {Array} results
   */
  renderNextPage(results: SearchResults) {
    const template = document.getElementById(RESULTS_TEMPLATE_ID)?.innerHTML;
    const output = mustache.render(template, {
      results: results,
      hasNextPage: this.hasNextPage
    });
    this.$results.append(output);
    this.repositionFacetNav();
  }

  /**
   * No results. Let'em down easy.
   */
  renderNoResults() {

    const template = document.getElementById(NO_RESULTS_TEMPLATE_ID)?.innerHTML || '';
    this.$results.html(template);
  }

  /**
   * Deliver the goods.
   * @param {Array} results
   * @param queryId The queryID that is returned from Algolia when clickAnalytics is set to true
   */
  renderResults(results: SearchResults, queryId: string) {
    const template = document.getElementById(RESULTS_TEMPLATE_ID)?.innerHTML;
    const output = mustache.render(template, {
      results: results,
      hasNextPage: this.hasNextPage
    });

    this.$results.html(output);

    // Bind onClick events to results that send click events to Algolia
    this.$results.on('click','.results-wrapper a', (event)=> {
      const objectId: string = $(event.currentTarget).attr('data-object-id') as string;
      const position: number = $(event.currentTarget).attr('data-position') as unknown as number;
      sendClickEvent(queryId, objectId, position);
    });
  }

  /**
   * Make the facets-nav sticky when and where it should.
   */
  repositionFacetNav() {
    var scrollTop = $(document).scrollTop();

    // Stick facets list to top of window when scrolling through results
    if (!browser.isMobile()) {
      if (this.$results.height() > this.$facetsList.height()) {
        const topOffset = (this.$globalHeader.outerHeight() + this.$searchForm.outerHeight() + 50);
        const bottomOffset = this.$globalFooter.outerHeight();

        if (scrollTop >= topOffset) {
          this.$facetsList.addClass(FIXED_MENU_CLASS);

          if ($(window).height() < (bottomOffset + this.$facetsList.outerHeight() ) && ( (scrollTop + $(window).height()) >= ($(document).height() - (bottomOffset + 30)) )) {
            this.$facetsList.addClass(FIXED_MENU_BOTTOM_CLASS);
          } else {
            this.$facetsList.removeClass(FIXED_MENU_BOTTOM_CLASS);
          }
        } else {
          this.$facetsList.removeClass(FIXED_MENU_CLASS);
        }
      }
    }
  }

  /**
   * Actually perform the search.
   * @param {String} query
   * @param {String} facet
   */
  search(query: string, facet: string) {

    // If a query is available.
    if (query) {

      this.setState(LOADING_STATE);

      query = decodeURIComponent(query);

      this.searchHelper.setQuery(query);

      // We have to remove any facet refinements so they're replaced and not added to the request.
      this.searchHelper.removeFacetRefinement(ALGOLIA_FACET_KEY);
      if (facet) {
        this.searchHelper.addFacetRefinement(ALGOLIA_FACET_KEY, facet);
      }

      this.$searchInput.val(query.trim());
      this.searchHelper.search();
    }
  }

  /**
   * Umm... set facet-nav width.
   */
  setFacetWidth() {
    const parentWidth = this.$facetsList.parent('.facets-column').width();
    this.$facetsList.width(parentWidth);
  }

  /**
   * The state of the "search" application (loading, no-results, etc.)
   * Not to be confused with "searchHelper" state.
   *
   * @param {String} newState
   */
  setState(newState) {
    if (SEARCH_STATE.hasOwnProperty(newState)) {
      Object.keys(SEARCH_STATE).forEach(state => {
        const stateClass = SEARCH_STATE[state];
        this.$searchContainer.removeClass(stateClass);
      });

      this.$searchContainer.addClass(SEARCH_STATE[newState]);
    }
  }
}

/**
 * Let's do this...
 */
new WacSearch().init();
